/**
 * Centralized helpers for the silent-refresh dance.
 *
 *  - `bootRefresh()` runs at cold load. On success it stores the bearer; on
 *    failure it returns false and the caller redirects (§5.1).
 *  - `redirectToCentralizedLogin()` stashes the deep link in sessionStorage
 *    and `window.location.assign`s to the centralized-login URL with the
 *    `returnUrl` query (§2.7.2).
 */
import { authApi } from '@/api/auth';
import { ApiError } from '@/api/errors';
import { setAccessToken, setCsrfToken } from './authStore';
import { env } from '@/config/env';

const DEEP_LINK_KEY = 'saas_admin.deepLink';

export async function bootRefresh(): Promise<boolean> {
  try {
    const body = await authApi.refresh();
    if (body.accessToken) {
      setAccessToken(body.accessToken, body.expiresAt ?? null);
      // Capture the CSRF token from the FLAT response body (no `.data`
      // wrapper). Authoritative source for /api/saas-admin/** mutations
      // since the journix_csrf cookie is cross-origin and unreadable
      // from JS at saas-admin.journix.ai. See specs/0003.
      setCsrfToken(body.csrfToken ?? null);
      return true;
    }
    return false;
  } catch (err) {
    if (err instanceof ApiError) {
      // 401 / 403 / network are all "not authed" for boot.
      return false;
    }
    return false;
  }
}

export function stashDeepLink(path: string): void {
  try {
    sessionStorage.setItem(DEEP_LINK_KEY, path);
  } catch {
    /* ignore quota / disabled storage */
  }
}

export function popDeepLink(): string | null {
  try {
    const v = sessionStorage.getItem(DEEP_LINK_KEY);
    sessionStorage.removeItem(DEEP_LINK_KEY);
    return v;
  } catch {
    return null;
  }
}

export function clearDeepLink(): void {
  try {
    sessionStorage.removeItem(DEEP_LINK_KEY);
  } catch {
    /* ignore */
  }
}

export function centralizedLoginUrl(returnUrl: string): string {
  const base = env.centralizedLoginUrl.replace(/\/$/, '');
  const path = env.centralizedLoginPath || '';
  const sep = path.includes('?') ? '&' : '?';
  return `${base}${path}${sep}returnUrl=${encodeURIComponent(returnUrl)}`;
}

export function redirectToCentralizedLogin(currentPath: string): void {
  stashDeepLink(currentPath);
  const returnUrl = `${env.saasAdminOrigin}/auth/return`;
  if (typeof window !== 'undefined') {
    window.location.assign(centralizedLoginUrl(returnUrl));
  }
}
